/**
 * product-essentials-financing.js
 * Ligne « Financer » du bloc sticky (fiche produit, parcours standard).
 *
 * Ouvre le MÊME tunnel de location Greenleaze que l'ancien bouton « Louer » du bloc LLD.
 * Reproduit `openAbonnementModal()` (assets/product-louer-block.js) et le listener de
 * `.greenleaze-button-product` (snippets/greenleaze-abonnement-product.liquid), dans le
 * MÊME ordre de repli — l'ordre est le contrat, pas un détail :
 *   1. tunnel direct `window.Greenleaze.openRentalFlow` si le réglage l'active ;
 *   2. modale location native `window.GreenleazeLocationModal.open` (refonte LLD) ;
 *   3. modale de durée en iframe `#modal-abonnement-product--greenleaze`.
 * Chaque étage ne s'exécute que si le précédent a renvoyé faux, donc « Financer » suit
 * automatiquement les réglages marchand sans condition dupliquée ici.
 *
 * ⚠️ L'étage 2 reste INERTE tant que la refonte de la modale location (branche
 * feat/refonte_modale_location_869e2z07b) n'est pas intégrée : `GreenleazeLocationModal`
 * est alors undefined et le clic retombe sur l'iframe, comportement d'avant.
 *
 * Pourquoi un handler dédié plutôt que la classe `.greenleaze-button-product` :
 *  - le CSS legacy de cette classe (mint.css:5116-5134) impose width:100%, une bordure
 *    et surtout un `:hover { background:#1a1a1a !important }` qui repeignait la ligne en
 *    noir plein largeur — la neutraliser aurait exigé du `!important` ;
 *  - l'ouverture n'était câblée que par `addModalAndButtonPrice` (greenleazePriceActualize),
 *    dont le timing laissait le clic sans effet. Ce handler la rend déterministe.
 * Le tracking du clic reste porté par le délégué dataLayer (tracking-ecommerce.liquid,
 * cta_name=financing_lld), qui lit la mensualité affichée — rien à pousser ici.
 *
 * Dépendance identique à l'ancien « Louer » : `window.Greenleaze.openRentalFlow` et/ou la
 * modale sont fournis par `greenleaze-abonnement-product` (rendu sur les fiches louables).
 * Absents (produit non louable) → no-op silencieux, comme le legacy.
 */
(function () {
  'use strict';
  if (window.__mbFinancingLldWired) return;
  window.__mbFinancingLldWired = true;

  function openRentalTunnel(variantId, trigger) {
    if (
      window.Greenleaze &&
      typeof window.Greenleaze.openRentalFlow === 'function' &&
      window.Greenleaze.openRentalFlow({ duration: 36, variantId: variantId })
    ) {
      return;
    }
    // Modale location native (LLD) : ouverte seulement si activée pour l'appareil courant
    // (réglage greenleaze_location_native_devices). open() renvoie false si la modale
    // n'est pas rendue, si l'appareil n'est pas ciblé ou si aucun prix n'est disponible
    // → on retombe sur l'iframe, exactement comme le CTA « Louer ».
    if (
      window.GreenleazeLocationModal &&
      window.GreenleazeLocationModal.open({
        duration: 36,
        variantId: variantId,
        trigger: trigger,
      })
    ) {
      return;
    }
    var modal = document.querySelector('#modal-abonnement-product--greenleaze');
    if (modal) modal.style.display = 'block';
  }

  // Cible la CARTE (`__financing-row`), pas le seul libellé : depuis le 2026-08-07 c'est
  // toute la ligne « Ou financer · Dès X €/mois » qui ouvre le tunnel, montant compris.
  // `data-variant-id` vit donc sur la carte — le lire sur `__financing-link` renverrait
  // undefined et ferait ouvrir le tunnel sur la mauvaise variante.
  document.addEventListener('click', function (e) {
    var card = e.target.closest('.product-essentials-block__financing-row');
    if (!card) return;
    e.preventDefault();
    openRentalTunnel(card.dataset.variantId, card);
  });

  // --- Mensualité affichée : resynchronisation sur GreenLeaze ---------------
  // Le premier paint vient du metafield lld.mensualite_36_mois (rendu serveur, zéro
  // CLS), mais ce metafield peut DIVERGER du prix réellement calculé par GreenLeaze
  // (mesuré 2026-07-30, welt-g80 / L : metafield 30 €, GreenLeaze 24 €). GreenLeaze
  // fait foi — c'est déjà la source du bandeau haut mobile, dont on reprend le patron
  // à l'identique (assets/lld-header-banner-price.js) pour que les deux montants ne
  // puissent plus afficher deux prix différents sur la même page.
  var block = document.querySelector('.product-essentials-block--financing');
  var amountEl = block && block.querySelector('[data-financing-amount]');
  if (!block || !amountEl) return;

  var state = {
    priceTTC: parseInt(block.dataset.price, 10),
    taxable: block.dataset.taxable === 'true',
  };

  function priceHT() {
    if (isNaN(state.priceTTC)) return null;
    return state.priceTTC / (state.taxable ? 1.2 : 1) / 100;
  }


  // Mensualité À AFFICHER : passe par getDisplayPriceForProduct, qui applique le MÊME
  // plancher d'apport que la modale de location (20 % du prix sur les vélos en TVA sur
  // marge) et amortit le supplément par les règles Greenleaze. Sans lui, ce montant repart
  // sur l'apport Greenleaze par défaut et annonce une mensualité PLUS ÉLEVÉE que la modale
  // (Cervélo 3 999 € : 131 € ici, 121,33 € dans la modale — mesuré le 22/09/2026).
  // `marginVat` = variante NON taxable, soit exactement le booléen qui pilote priceHT().
  // Repli sur getAllPricesForProduct si le CDN sert un greenleaze-script.js antérieur à cet
  // ajout : ancien montant (divergent), jamais de montant vide.
  function monthlyForDisplay(ht) {
    var displayed = isNaN(state.priceTTC) ? 0 : state.priceTTC / 100;
    if (typeof window.getDisplayPriceForProduct === 'function') {
      return window.getDisplayPriceForProduct(ht, 36, displayed, !state.taxable).monthly;
    }
    var prices = window.getAllPricesForProduct(ht) || {};
    return prices[36] && prices[36].monthly;
  }

  function syncAmount() {
    if (typeof window.getAllPricesForProduct !== 'function') return;
    var ht = priceHT();
    if (!ht) return;
    var monthly = monthlyForDisplay(ht);
    if (monthly) {
      amountEl.textContent = Math.round(monthly).toLocaleString('fr-FR');
      block.style.display = '';
    } else {
      // Variante sans offre 36 mois : masquer plutôt que laisser un montant faux.
      // Même arbitrage que le bandeau mobile. Pas de CLS au chargement : le gate
      // Liquid n'émet le bloc que si le metafield est renseigné, donc ce chemin ne
      // se déclenche qu'après un changement de variante par l'utilisateur.
      block.style.display = 'none';
    }
  }

  document.addEventListener('variant:change', function (e) {
    var v = e && e.detail && e.detail.variant;
    if (!v || !v.price) return;
    state.priceTTC = parseInt(v.price, 10);
    block.dataset.price = v.price;
    syncAmount();
  });

  window.addEventListener('greenleazePriceActualize', syncAmount);
  // Indispensable : ce script est `defer`, GreenLeaze est async. Quand GreenLeaze
  // gagne la course, 'greenleazePriceActualize' est déjà passé et n'est jamais
  // rejoué → sans cet appel le montant resterait figé sur le metafield.
  syncAmount();
})();
